import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Alert,
  Divider,
  message,
  Tag,
  Empty,
  Tooltip,
  Grid,
  theme,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ToolOutlined,
  ThunderboltFilled,
  LinkOutlined,
  CheckOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'libs/dayjs';
import HallazgoChip from 'components/admin/Hallazgos/HallazgoChip';
import AscensorInlineSelect from 'components/selects/AscensorInlineSelect';
import { ImportExportToolbar } from 'components/global/ImportExport';
import { ORDEN_TAREA_COLUMNS } from 'components/global/ImportExport/ordenTareaColumns';
import { buildOrderTasksPayload } from './buildOrderTasksPayload';
import {
  generateOrderFromBudgetApi,
  updateOrderFromBudgetApi,
  getOrderWithItemsApi,
} from 'api/hallazgoFlujo';
import { patchOrderHeaderApi } from 'api/odtGantt';
import { useTenantColors } from 'hooks/useTenantColors';
import { useUser } from 'providers/Users/UserProvider';

// Restringe el arrastre al eje Y (mismo criterio que SortableTabsTable, evita
// sumar @dnd-kit/modifiers solo por esto).
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

// Envuelve una fila de tarea del Form.List para hacerla arrastrable. El handle
// (icono) recibe los listeners; el resto de la fila queda editable con normalidad.
//
// `compact` (pantallas < md) cambia la caja de tres columnas
// [handle | campos | eliminar] a una sola columna: el handle y el botón de
// eliminar suben a una cabecera propia y los campos se quedan con todo el ancho.
// A 390 px las tres columnas dejaban la descripción en 111 px y el material en 80.
function SortableTaskCard({ id, secondary, compact, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    padding: 12,
    background: '#f6f7f9',
    border: '1px solid #e2e6eb',
    borderRadius: 8,
    borderLeft: `3px solid ${secondary}`,
    ...(compact
      ? { display: 'flex', flexDirection: 'column', gap: 8 }
      : { display: 'grid', gridTemplateColumns: '28px 1fr 32px', gap: 10, alignItems: 'start' }),
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 9999, position: 'relative' } : {}),
  };
  return children({ setNodeRef, style, handleProps: { ...attributes, ...listeners } });
}

const hexToRgba = (hex, alpha = 0.18) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(148, 163, 184, ${alpha})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Fase 5 del roadmap Hallazgos->Presupuesto->ODT.
// Drawer que convierte un presupuesto aprobado en una ODT ejecutable.
// Cada tarea puede vincularse a:
//   - un hallazgo especifico del presupuesto (para cierre en cascada Fase 6)
//   - un material/servicio del catalogo (id_coleccion_item)
//
// Pre-llenado: si el presupuesto trae budgetitem, cada item se pre-carga como
// tarea. Si trae hallazgos, el selector permite asignarlos a cada tarea.
//
// Al guardar llama POST /orders/from-budget y dispara onSuccess.

function GenerateOrderDrawer({
  open,
  budget, // { id, status, BudgetItems?, Hallazgos? }
  idClient, // id del cliente del budget — usado por AscensorInlineSelect
  hallazgos = [], // hallazgos vinculados al budget (deben provenir del budget)
  presupuestoItems = [], // budgetitem pre-cargados (modo creacion)
  existingOrder = null, // { id, assigned_to_user_id, fecha_comprometida, items: [{id, description, elevator, id_hallazgo, ...}], ... } — modo edicion.
  // El padre (Budget.jsx) arma este objeto propagando toda la respuesta de
  // getOrderWithItemsApi (no una lista blanca escrita a mano) y le agrega
  // `id` y `assigned_to_user_id`, que ese endpoint no trae.
  catalogoItems = [], // coleccion_items disponibles para asignar como material
  onClose,
  onSuccess,
}) {
  const isEdit = Boolean(existingOrder?.id);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  // Catálogo de equipos del cliente, capturado vía onListLoaded de los
  // AscensorInlineSelect (que ya lo bajan). Usado para resolver nombres → IDs
  // al submit (Fase 4 plan trazabilidad-equipos). Si no cargó o un nombre no
  // resuelve, cada tarea cae al path legacy (elevator string).
  const [clientAscensores, setClientAscensores] = useState([]);
  const { secondary } = useTenantColors();
  const { token } = theme.useToken();
  const secondaryTint = hexToRgba(secondary, 0.18);
  // Bajo `md` (768 px) el drawer ocupa todo el ancho y las tarjetas de tarea se
  // apilan: con `width={760}` fijo, en un teléfono de 390 px el panel quedaba
  // anclado a la derecha y se salía 370 px por el borde izquierdo.
  const screens = Grid.useBreakpoint();
  const compact = !screens.md;
  const ctlSize = compact ? 'middle' : 'small';
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const userCtx = useUser();
  const especialistas = userCtx?.especialistas ?? [];

  // Cargar especialistas la primera vez que el drawer se abre.
  useEffect(() => {
    if (open && userCtx && !userCtx.hasCalled && !userCtx.isLoading) {
      userCtx.call?.();
    }
  }, [open, userCtx]);

  // Invalidar el catálogo si cambia el cliente: los nombres son scoped por
  // cliente, no queremos resolver contra el catálogo del cliente anterior.
  useEffect(() => {
    setClientAscensores([]);
  }, [idClient]);

  // Normaliza el elevator del backend (string) al array que espera el
  // multi-select del form. Si ya viene vacio devuelve []; si es un string
  // con comas (patron legacy) lo splitea y trimea.
  const toElevatorArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    return String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Mapea order_item (backend) a la shape de tarea del Form.List: filtra
  // ocultos (status_id=10) y resuelve el elevator string legacy a array.
  // Reusado por el precargado inicial (useEffect) y por
  // refrescarTareasImportadas (post-import desde el toolbar de import/export).
  const mapOrderItemsToTasks = (items) => {
    const visibleItems = (items || []).filter((it) => (it.status_id ?? it.item_status_id) !== 10);
    return visibleItems.map((it) => ({
      id: it.id ?? it.id_item,
      description: it.description || '',
      elevator: toElevatorArray(it.elevator),
      cantidad: 1,
      id_hallazgo: it.id_hallazgo ?? it.Hallazgo?.id ?? null,
      id_coleccion_item: it.id_coleccion_item ?? null,
    }));
  };

  useEffect(() => {
    if (!open) return;
    let initialTasks = [];
    let initialManager = '';
    if (isEdit) {
      // Modo edicion: precargar desde order_item (filtrar ocultos status_id=10).
      initialTasks = mapOrderItemsToTasks(existingOrder.items);
      // Migración rut→id: el Select usa el id del técnico; precargar la companion.
      initialManager = existingOrder.assigned_to_user_id || '';
    } else if (presupuestoItems.length > 0) {
      initialTasks = presupuestoItems.map((pi) => ({
        description: pi.description || '',
        elevator: toElevatorArray(pi.elevator),
        cantidad: pi.quantity || 1,
        // BUG 3: el hallazgo viene del propio budgetitem (persistido al crear el
        // presupuesto), no del emparejamiento por índice hallazgos[idx] (frágil:
        // misasignaba si los ítems no eran 1:1 con los hallazgos).
        id_hallazgo: pi.id_hallazgo ?? null,
        id_coleccion_item: pi.id_coleccion_item ?? null,
      }));
    } else if (hallazgos.length > 0) {
      initialTasks = hallazgos.map((h) => ({
        description: `Resolver: ${h.descripcion}`.slice(0, 200),
        elevator: h.Ascensor?.nombre ? [h.Ascensor.nombre] : [],
        cantidad: 1,
        id_hallazgo: h.id,
        id_coleccion_item: null,
      }));
    } else {
      initialTasks = [{ description: '', elevator: [], cantidad: 1 }];
    }
    const initialFecha =
      isEdit && existingOrder?.fecha_comprometida ? dayjs(existingOrder.fecha_comprometida) : null;
    form.resetFields();
    form.setFieldsValue({
      orderManager: initialManager,
      fecha_comprometida: initialFecha,
      tasks: initialTasks,
    });
  }, [open, presupuestoItems, hallazgos, existingOrder, isEdit, form]);

  const tasks = Form.useWatch('tasks', form) || [];

  const hallazgoOpts = useMemo(
    () =>
      hallazgos.map((h) => ({
        value: h.id,
        label: `#${h.id} · ${h.descripcion.slice(0, 60)}${h.descripcion.length > 60 ? '…' : ''}`,
        severidad: h.severidad,
      })),
    [hallazgos]
  );

  const catalogoOpts = useMemo(
    () =>
      catalogoItems.map((c) => ({
        value: c.id,
        label: `${c.nombre} · ${c.tipo_item}${
          c.precio ? ` · ${c.moneda || 'CLP'} ${c.precio}` : ''
        }`,
      })),
    [catalogoItems]
  );

  // Hallazgos sin cobertura: no aparecen en ninguna tarea.
  const uncoveredHallazgos = useMemo(() => {
    const covered = new Set(tasks.map((t) => t?.id_hallazgo).filter((v) => v != null));
    return hallazgos.filter((h) => !covered.has(h.id));
  }, [tasks, hallazgos]);

  const canSubmit = budget && tasks.length > 0 && !submitting;

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    const values = form.getFieldsValue();

    // Arma el payload: expansión multi-ascensor + resolución nombre → id_ascensor
    // (FK canonical) con fallback a elevator string. Ver buildOrderTasksPayload.
    const tasksPayload = buildOrderTasksPayload(values.tasks, clientAscensores, isEdit);

    let result;
    if (isEdit) {
      result = await updateOrderFromBudgetApi(budget.id, {
        orderManager: values.orderManager || null,
        tasks: tasksPayload,
      });
    } else {
      result = await generateOrderFromBudgetApi({
        idBudget: budget.id,
        orderManager: values.orderManager || undefined,
        tasks: tasksPayload,
      });
    }

    // Nivel simple de planificación: la fecha de término comprometida vive en la
    // cabecera de la ODT, no en el payload de tareas. Se persiste con el id de la
    // ODT (recién creada o existente) sin encender la carta Gantt.
    //
    // En edición se envía siempre (haya o no fecha elegida): el campo ahora se
    // precarga desde /order/name/items, así que vaciarlo es una acción explícita
    // del usuario y debe viajar como null para borrar la fecha ya guardada. En
    // creación no hay fecha previa que borrar, así que solo se envía si el
    // usuario eligió una (evita una llamada de más).
    //
    // patchOrderHeaderApi nunca lanza: ante un fallo (ODT ya finalizada, corte de
    // red, etc.) devuelve un objeto con `codeMessage` en vez de la cabecera
    // actualizada. Si no se revisa el retorno, el usuario ve "ODT actualizada"
    // aunque la fecha no se haya guardado — las tareas sí quedaron guardadas
    // (ese llamado es independiente), así que el aviso debe ser preciso sobre
    // qué parte falló.
    let fechaNoGuardada = false;
    if (result?.idOrder && (isEdit || values.fecha_comprometida)) {
      const fecha = values.fecha_comprometida
        ? dayjs(values.fecha_comprometida).format('YYYY-MM-DD')
        : null;
      const fechaRes = await patchOrderHeaderApi(result.idOrder, { fecha_comprometida: fecha });
      fechaNoGuardada = Boolean(fechaRes?.codeMessage);
    }

    setSubmitting(false);

    if (result?.idOrder) {
      if (fechaNoGuardada) {
        message.warning(
          `ODT #${result.idOrder}: las tareas se guardaron, pero la fecha de término comprometida no se pudo actualizar. Volver a intentarlo.`
        );
      } else if (isEdit) {
        message.success(
          `ODT #${result.idOrder} actualizada — ${result.itemsActualizados} editadas, ${result.itemsCreados} nuevas, ${result.itemsOcultos} ocultas`
        );
      } else {
        message.success(
          `ODT #${result.idOrder} creada — ${result.tareasCreadas} tareas, ${result.hallazgosTransicionados} hallazgos en EN_ODT`
        );
      }
      onSuccess?.(result);
    } else {
      message.error(result?.message || 'No se pudo guardar la ODT');
    }
  };

  // Post-import (ImportExportToolbar del drawer, modo edición): re-fetchea la
  // ODT con sus items y repuebla el Form.List en el lugar. A diferencia del
  // Gantt, este toolbar vive dentro del propio drawer de edición — no puede
  // llamar a onSuccess porque el padre (Budget.jsx) lo usa para cerrar el
  // drawer, y acá lo que corresponde es refrescar sin cerrar.
  const refrescarTareasImportadas = async () => {
    const res = await getOrderWithItemsApi(existingOrder.id);
    if (res?.state !== 'success') {
      message.error(res?.message || 'No se pudieron recargar las tareas importadas');
      return;
    }
    form.setFieldsValue({ tasks: mapOrderItemsToTasks(res.order?.items || []) });
    message.success('Tareas importadas');
  };

  // Mismas acciones en los dos layouts: van al `extra` de la cabecera en
  // escritorio y al `footer` en pantalla chica (ver el prop `footer` abajo).
  const acciones = (
    <>
      <Button onClick={onClose} disabled={submitting} block={compact}>
        Cancelar
      </Button>
      <Button
        type="primary"
        loading={submitting}
        disabled={!canSubmit}
        onClick={handleSubmit}
        icon={<ThunderboltFilled />}
        block={compact}
        style={{ background: secondary, borderColor: secondary, marginLeft: compact ? 0 : 8 }}
      >
        {isEdit ? 'Guardar cambios' : 'Crear ODT'}
      </Button>
    </>
  );

  return (
    <Drawer
      title={
        <Space size="middle" align="center">
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: secondaryTint,
              color: secondary,
            }}
          >
            <ToolOutlined style={{ fontSize: 18 }} />
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: token.colorTextBase,
              }}
            >
              {isEdit ? 'Editar ODT' : 'Generar ODT desde presupuesto'}
            </div>
            {budget ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Presupuesto #{budget.id} · {budget.status}
                {isEdit && existingOrder?.id ? ` · ODT #${existingOrder.id}` : ''}
              </div>
            ) : null}
          </div>
        </Space>
      }
      placement="right"
      width={compact ? '100%' : 760}
      open={open}
      onClose={onClose}
      destroyOnHidden
      // En pantalla chica las acciones bajan al footer (fijo abajo, al alcance
      // del pulgar): con diez tareas en pantalla, el botón de crear en la barra
      // superior queda a un scroll de distancia.
      extra={compact ? undefined : acciones}
      footer={compact ? acciones : undefined}
      footerStyle={compact ? { display: 'flex', gap: 8 } : undefined}
    >
      {/* ====== Hallazgos del presupuesto ====== */}
      {hallazgos.length > 0 ? (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
              HALLAZGOS VINCULADOS · {hallazgos.length}
            </Typography.Text>
            {uncoveredHallazgos.length > 0 ? (
              <Tag color="warning">{uncoveredHallazgos.length} sin tarea asignada</Tag>
            ) : (
              <Tag color="success" icon={<CheckOutlined />}>
                Todos cubiertos
              </Tag>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {hallazgos.map((h) => (
              <HallazgoChip key={h.id} hallazgo={h} />
            ))}
          </div>
          <Typography.Text
            style={{
              display: 'block',
              marginTop: 10,
              fontSize: 12,
              color: '#64748b',
            }}
          >
            Al finalizar la ODT, cada tarea con hallazgo vinculado cierra ese hallazgo
            automáticamente (cascada Fase 6).
          </Typography.Text>
        </div>
      ) : null}

      {/* ====== Form ====== */}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="orderManager"
          label="Técnico asignado (opcional)"
          style={{ maxWidth: 360 }}
        >
          <Select
            allowClear
            showSearch
            placeholder="Seleccionar técnico..."
            optionFilterProp="label"
            loading={userCtx?.isLoading}
            options={especialistas.map((u) => ({
              value: u.id,
              label: `${u.fullname} · ${u.rut}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="fecha_comprometida"
          label="Fecha de término comprometida (opcional)"
          style={{ maxWidth: 360 }}
          extra="La fecha en que se compromete a tener la ODT terminada. Para planificar tarea por tarea, usa la carta Gantt."
        >
          <DatePicker format="DD/MM/YYYY" placeholder="Elige una fecha" style={{ width: '100%' }} />
        </Form.Item>

        {isEdit && existingOrder?.id ? (
          <div style={{ marginBottom: 12 }}>
            <ImportExportToolbar
              scope={{ level: 'order', idOrder: Number(existingOrder.id) }}
              columns={ORDEN_TAREA_COLUMNS}
              config={{
                exportUrl: (_s, format) =>
                  `/orders/${existingOrder.id}/tareas/export?format=${format}`,
                validateUrl: `/orders/${existingOrder.id}/tareas/import/validate`,
                commitUrl: `/orders/${existingOrder.id}/tareas/import/commit`,
              }}
              humanName="tareas"
              onCommitSuccess={refrescarTareasImportadas}
            />
          </div>
        ) : null}

        <Divider style={{ margin: '20px 0 12px' }}>
          <Typography.Text strong style={{ fontSize: 11, letterSpacing: 0.8, color: '#64748b' }}>
            TAREAS DE LA ODT
          </Typography.Text>
        </Divider>

        <Form.List name="tasks">
          {(fields, { add, remove, move }) => (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;
                const oldIndex = fields.findIndex((f) => f.key === active.id);
                const newIndex = fields.findIndex((f) => f.key === over.id);
                if (oldIndex < 0 || newIndex < 0) return;
                move(oldIndex, newIndex);
              }}
            >
              <SortableContext
                items={fields.map((f) => f.key)}
                strategy={verticalListSortingStrategy}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {fields.map((field, idx) => (
                    <SortableTaskCard
                      key={field.key}
                      id={field.key}
                      secondary={secondary}
                      compact={compact}
                    >
                      {({ setNodeRef, style, handleProps }) => (
                        <div ref={setNodeRef} style={style}>
                          <div
                            style={
                              compact
                                ? { display: 'flex', alignItems: 'center', gap: 6 }
                                : {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 4,
                                    paddingTop: 4,
                                  }
                            }
                          >
                            <Button
                              type="text"
                              size={ctlSize}
                              icon={<HolderOutlined />}
                              style={{ cursor: 'grab', touchAction: 'none' }}
                              aria-label="Reordenar tarea"
                              {...handleProps}
                            />
                            <span
                              style={{
                                fontFamily: "'IBM Plex Mono', monospace",
                                color: '#94a3b8',
                                fontSize: 11,
                              }}
                            >
                              {compact
                                ? `Tarea ${String(idx + 1).padStart(2, '0')}`
                                : String(idx + 1).padStart(2, '0')}
                            </span>
                            {compact ? (
                              <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                                aria-label="Quitar tarea"
                                style={{ marginLeft: 'auto' }}
                              />
                            ) : null}
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              // La descripción y el hallazgo ocupan la fila entera:
                              // son los dos campos con texto largo. El resto se
                              // reparte las columnas. En compacto todo va apilado.
                              gridTemplateColumns: compact ? '1fr' : '2fr 1fr 80px',
                              gap: 8,
                            }}
                          >
                            <Form.Item
                              {...field}
                              key={`${field.key}-description`}
                              name={[field.name, 'description']}
                              style={{
                                marginBottom: 0,
                                gridColumn: compact ? undefined : '1 / -1',
                              }}
                              rules={[{ required: true, message: 'Descripción requerida' }]}
                              // El área crece por ajuste de línea, pero la descripción
                              // sigue siendo de una sola línea como dato: el parser CSV
                              // del import de tareas (API-PROAPP parsers.ts, parseCsv)
                              // corta por salto de línea ANTES de separar por ';', así que
                              // una descripción multilínea rompe el round-trip del Excel.
                              normalize={(v) =>
                                typeof v === 'string' ? v.replace(/[\r\n]+/g, ' ') : v
                              }
                            >
                              {/* Es el único campo que dice QUÉ hay que hacer; el resto son
                                  datos de apoyo. Va en fila propia, con cuerpo mayor y como
                                  área que crece con el texto: la precarga desde hallazgos
                                  llega a 200 caracteres y en una línea de 390 px se veía
                                  menos de un cuarto. */}
                              <Input.TextArea
                                placeholder="Tarea a ejecutar"
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.preventDefault();
                                }}
                                style={{ fontSize: compact ? 15 : 14, fontWeight: 500 }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`${field.key}-elevator`}
                              name={[field.name, 'elevator']}
                              style={{ marginBottom: 0 }}
                              rules={[
                                {
                                  validator: (_, val) =>
                                    Array.isArray(val) && val.length > 0
                                      ? Promise.resolve()
                                      : Promise.reject(new Error('Ubicación requerida')),
                                },
                              ]}
                              extra={
                                Array.isArray(tasks[idx]?.elevator) &&
                                tasks[idx].elevator.length > 1 ? (
                                  <span style={{ color: secondary, fontSize: 11 }}>
                                    Se crearán {tasks[idx].elevator.length} tareas (una por
                                    ubicación)
                                  </span>
                                ) : null
                              }
                            >
                              <AscensorInlineSelect
                                mode="multiple"
                                idClient={idClient}
                                size={ctlSize}
                                placeholder="Ascensor o ubicación"
                                onListLoaded={setClientAscensores}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`${field.key}-cantidad`}
                              name={[field.name, 'cantidad']}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                min={0}
                                placeholder="Cant."
                                size={ctlSize}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`${field.key}-id_hallazgo`}
                              name={[field.name, 'id_hallazgo']}
                              style={{
                                marginBottom: 0,
                                gridColumn: compact ? undefined : '1 / -1',
                              }}
                            >
                              <Select
                                allowClear
                                showSearch
                                size={ctlSize}
                                placeholder={
                                  <span>
                                    <LinkOutlined style={{ marginRight: 6 }} />
                                    Hallazgo que resuelve (opcional)
                                  </span>
                                }
                                optionFilterProp="label"
                                options={hallazgoOpts}
                              />
                            </Form.Item>
                            <Form.Item
                              {...field}
                              key={`${field.key}-id_coleccion_item`}
                              name={[field.name, 'id_coleccion_item']}
                              style={{ marginBottom: 0 }}
                            >
                              <Select
                                allowClear
                                showSearch
                                size={ctlSize}
                                placeholder="Material"
                                optionFilterProp="label"
                                options={catalogoOpts}
                              />
                            </Form.Item>
                          </div>
                          {/* En compacto este botón ya vive en la cabecera de la tarjeta. */}
                          {compact ? null : (
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              size="small"
                              onClick={() => remove(field.name)}
                              aria-label="Quitar tarea"
                            />
                          )}
                        </div>
                      )}
                    </SortableTaskCard>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        description: '',
                        elevator: '',
                        cantidad: 1,
                        id_hallazgo: null,
                        id_coleccion_item: null,
                      })
                    }
                    block
                  >
                    Agregar tarea
                  </Button>
                </div>
              </SortableContext>
            </DndContext>
          )}
        </Form.List>
      </Form>

      {tasks.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin tareas. Agregá al menos una para generar la ODT."
          style={{ marginTop: 20 }}
        />
      ) : null}

      {uncoveredHallazgos.length > 0 && hallazgos.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message={`${uncoveredHallazgos.length} hallazgo(s) sin tarea asignada`}
          description={
            <div>
              Estos hallazgos transicionarán a EN_ODT igual (junto al resto), pero quedarán abiertos
              hasta que una tarea los resuelva. Podés asignarlos ahora o dejar que queden pendientes
              de una segunda ODT.
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {uncoveredHallazgos.map((h) => (
                  <HallazgoChip key={h.id} hallazgo={h} compact />
                ))}
              </div>
            </div>
          }
        />
      ) : null}
    </Drawer>
  );
}

export default GenerateOrderDrawer;
